import type {AxiosError} from "axios";

import type {
  LicenseComplianceReason,
  OpenWorkAuthorizationRestriction,
  SecurityLicenseRequiredErrorData,
} from "@/src/modules/employee-hr/types";

export const SECURITY_LICENSE_REQUIRED_ERROR_CODE = "security_license_required";

type SecurityLicenseRequiredErrorBody = SecurityLicenseRequiredErrorData & {
  message?: string;
  error_code?: string;
};

export type SecurityLicenseBlockDisplay = {
  title: string;
  blocked_reasons: LicenseComplianceReason[];
  restriction: OpenWorkAuthorizationRestriction | null;
};

function asAxiosError(
  error: unknown,
): AxiosError<SecurityLicenseRequiredErrorBody> | null {
  if (!error || typeof error !== "object" || !("isAxiosError" in error)) return null;
  return error as AxiosError<SecurityLicenseRequiredErrorBody>;
}

/** Expected business rejection on POST shift/start when license compliance blocks the officer (403). */
export function isSecurityLicenseRequiredError(error: unknown): boolean {
  const axiosError = asAxiosError(error);
  if (!axiosError) return false;
  if (axiosError.response?.status !== 403) return false;
  return axiosError.response.data?.error_code === SECURITY_LICENSE_REQUIRED_ERROR_CODE;
}

export function getSecurityLicenseBlockDisplay(error: unknown): SecurityLicenseBlockDisplay {
  const axiosError = asAxiosError(error);
  const data = axiosError?.response?.data;
  const apiMessage = data?.message?.trim();

  return {
    title:
      apiMessage ||
      "You cannot start a shift until your security license requirements are met.",
    blocked_reasons: data?.blocked_reasons ?? [],
    restriction: data?.restriction ?? null,
  };
}
